<template>
  <div class="min-h-screen hs-page-backdrop">
    <div v-if="!embedded" class="px-6 pt-6">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex flex-wrap items-center gap-3">
          <hs-button
            variant="primary"
            icon="plus"
            @click="openCreateModal"
          >
            New Group
          </hs-button>
          <hs-button
            variant="secondary"
            :icon="loading ? 'loader' : 'refresh-cw'"
            :loading="loading"
            @click="refreshData"
          >
            Refresh
          </hs-button>
        </div>
      </div>
    </div>

    <div :class="['space-y-6', embedded ? '' : 'p-6']">
      <!-- Summary Stats -->
      <section class="admin-groups__stats">
        <hs-metric-card
          v-for="stat in summaryStats"
          :key="stat.label"
          :label="stat.label"
          :value="stat.value"
          :icon="stat.icon"
          :tone="stat.tone"
        />
      </section>

      <!-- Groups Table Card -->
      <div
          class="admin-groups__panel"
      >
        <!-- Toolbar -->
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6"
        >
          <div class="flex items-center gap-2">
            <hs-input
              v-model="search"
              class="flex-1 lg:w-72"
              type="search"
              icon="search"
              placeholder="Search groups by name..."
              @update:modelValue="onSearchInput"
            />
          </div>
          <div class="flex items-center gap-2">
            <hs-button
              variant="primary"
              icon="plus"
              @click="openCreateModal"
            >
              New Group
            </hs-button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="space-y-3">
          <div
            v-for="i in 5"
            :key="i"
            class="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-900"
          ></div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredGroups.length === 0"
          class="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center dark:border-gray-600 dark:bg-gray-900/30"
        >
          <div
            class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white shadow dark:bg-gray-800"
          >
            <i class="fas fa-layer-group text-xl text-gray-400"></i>
          </div>
          <h3
            class="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-100"
          >
            No groups found
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{
              search
                ? "No groups match your search."
                : "No groups have been created yet."
            }}
          </p>
        </div>

        <!-- Groups Table -->
        <div
          v-else
          class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <table class="w-full table-auto text-left text-sm">
            <thead
              class="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
            >
              <tr>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  ID
                </th>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Name
                </th>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Description
                </th>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Owner
                </th>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Created
                </th>
                <th
                  class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-right"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr
                v-for="group in filteredGroups"
                :key="group.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                <td
                  class="px-4 py-3 text-xs font-mono text-gray-500 dark:text-gray-400"
                >
                  {{ group.id }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="font-semibold text-gray-900 dark:text-white"
                  >{{ group.name }}</span>
                </td>
                <td
                  class="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-xs truncate"
                >
                  {{ group.description || "—" }}
                </td>
                <td class="px-4 py-3 text-gray-600 dark:text-gray-300">
                  {{ group.owner_name || group.owner_id || "—" }}
                </td>
                <td
                  class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ formatDate(group.created_at) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 focus:outline-none dark:text-green-400 dark:hover:bg-green-900/20"
                      title="View stores"
                      @click="openStoresModal(group)"
                    >
                      <i class="fas fa-store mr-1"></i>
                      Stores
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 focus:outline-none dark:text-blue-400 dark:hover:bg-blue-900/20"
                      title="Generate join code"
                      @click="openJoinCodeModal(group)"
                    >
                      <i class="fas fa-key mr-1"></i>
                      Join Code
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 focus:outline-none dark:text-gray-300 dark:hover:bg-gray-700"
                      title="Edit group"
                      @click="openEditModal(group)"
                    >
                      <i class="fas fa-edit mr-1"></i>
                      Edit
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 focus:outline-none dark:text-red-400 dark:hover:bg-red-900/20"
                      title="Delete group"
                      @click="openDeleteModal(group)"
                    >
                      <i class="fas fa-trash mr-1"></i>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          v-if="total > perPage"
          class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400"
        >
          <span>Showing {{ filteredGroups.length }} of {{ total }} groups</span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              :disabled="currentPage <= 1"
              class="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
              @click="changePage(currentPage - 1)"
            >
              Previous
            </button>
            <span>Page {{ currentPage }}</span>
            <button
              type="button"
              :disabled="filteredGroups.length < perPage"
              class="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
              @click="changePage(currentPage + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Group Modal -->
    <modal-view
      v-if="showCreateModal"
      title="Create Group"
      :custom-max-width="'540px'"
      @close-modal="showCreateModal = false"
    >
      <form class="px-6 pb-8 space-y-5" @submit.prevent="handleCreate">
        <div class="space-y-4">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Group Name *
            </label>
            <hs-input
              v-model="createForm.name"
              placeholder="e.g., Downtown Partners"
              required
              maxlength="120"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Description (optional)
            </label>
            <textarea
              v-model="createForm.description"
              rows="3"
              placeholder="Describe the purpose of this group…"
              class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            ></textarea>
          </div>
        </div>
        <div
          v-if="createError"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
        >
          {{ createError }}
        </div>
        <div class="flex items-center justify-end gap-3 pt-2">
          <hs-button
            variant="secondary"
            @click="showCreateModal = false"
          >
            Cancel
          </hs-button>
          <hs-button
            type="submit"
            variant="primary"
            icon="plus"
            :loading="createLoading"
            :disabled="createLoading || !createForm.name.trim()"
          >
            Create Group
          </hs-button>
        </div>
      </form>
    </modal-view>

    <!-- Edit Group Modal -->
    <modal-view
      v-if="showEditModal && editTarget"
      title="Edit Group"
      :custom-max-width="'540px'"
      @close-modal="showEditModal = false"
    >
      <form class="px-6 pb-8 space-y-5" @submit.prevent="handleUpdate">
        <div class="space-y-4">
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Group Name *
            </label>
            <hs-input
              v-model="editForm.name"
              placeholder="Group name"
              required
              maxlength="120"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Description (optional)
            </label>
            <textarea
              v-model="editForm.description"
              rows="3"
              placeholder="Describe the purpose of this group…"
              class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            ></textarea>
          </div>
        </div>
        <div
          v-if="editError"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
        >
          {{ editError }}
        </div>
        <div class="flex items-center justify-end gap-3 pt-2">
          <hs-button
            variant="secondary"
            @click="showEditModal = false"
          >
            Cancel
          </hs-button>
          <hs-button
            type="submit"
            variant="primary"
            icon="save"
            :loading="editLoading"
            :disabled="editLoading || !editForm.name.trim()"
          >
            Save Changes
          </hs-button>
        </div>
      </form>
    </modal-view>

    <!-- Delete Confirmation Modal -->
    <modal-view
      v-if="showDeleteModal && deleteTarget"
      title="Delete Group"
      :custom-max-width="'480px'"
      @close-modal="showDeleteModal = false"
    >
      <div class="px-6 pb-8 space-y-5">
        <div
          class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20"
        >
          <p class="text-sm text-red-800 dark:text-red-200">
            Are you sure you want to delete the group
            <span class="font-semibold">"{{ deleteTarget.name }}"</span>? This
            action cannot be undone.
          </p>
        </div>
        <div
          v-if="deleteError"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
        >
          {{ deleteError }}
        </div>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            :disabled="deleteLoading"
            class="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
            @click="handleDelete"
          >
            <i
              :class="[
                'fas mr-2',
                deleteLoading ? 'fa-sync fa-spin' : 'fa-trash',
              ]"
            ></i>
            Delete Group
          </button>
        </div>
      </div>
    </modal-view>

    <!-- Group Stores Modal -->
    <modal-view
      v-if="showStoresModal && selectedGroupForStores"
      :title="`Stores in &quot;${selectedGroupForStores.name}&quot;`"
      :custom-max-width="'760px'"
      @close-modal="closeStoresModal"
    >
      <div class="px-6 pb-8 space-y-4">
        <!-- Search -->
        <div class="relative">
          <input
            v-model="storesSearch"
            type="search"
            placeholder="Search stores by name…"
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pl-9 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            @input="onStoresSearchInput"
          />
          <span
            class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-400"
          >
            <i class="fas fa-search"></i>
          </span>
        </div>

        <!-- Loading -->
        <div v-if="storesLoading" class="space-y-2">
          <div
            v-for="i in 4"
            :key="i"
            class="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-900"
          ></div>
        </div>

        <!-- Error -->
        <div
          v-else-if="storesError"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200 flex items-center justify-between"
        >
          <span>{{ storesError }}</span>
          <button
            type="button"
            class="text-xs font-medium text-red-700 hover:text-red-800 dark:text-red-200 ml-4"
            @click="loadGroupStores"
          >
            Retry
          </button>
        </div>

        <!-- Empty -->
        <div
          v-else-if="groupStores.length === 0"
          class="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center dark:border-gray-600 dark:bg-gray-900/30"
        >
          <i class="fas fa-store text-3xl text-gray-400 mb-3"></i>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ storesSearch ? "No stores match your search." : "No stores in this group yet." }}
          </p>
        </div>

        <!-- Table -->
        <div
          v-else
          class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <table class="w-full table-auto text-left text-sm">
            <thead
              class="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
            >
              <tr>
                <th class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Store</th>
                <th class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Merchant Code</th>
                <th class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Role</th>
                <th class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Joined</th>
                <th class="px-4 py-3 font-medium text-gray-600 dark:text-gray-300 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr
                v-for="store in groupStores"
                :key="store.store_id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                <td class="px-4 py-3 font-medium text-gray-900 dark:text-white">
                  {{ store.name }}
                </td>
                <td class="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                  {{ store.merchant_code || "—" }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getStoreRoleBadgeClass(store.role)"
                  >
                    {{ store.role }}
                  </span>
                </td>
                <td class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {{ formatDate(store.created_at) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <!-- Inline confirm state -->
                  <template v-if="storeToRemove && storeToRemove.store_id === store.store_id">
                    <span class="text-xs text-gray-600 dark:text-gray-300 mr-2">Remove?</span>
                    <button
                      type="button"
                      :disabled="removingStore"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 focus:outline-none dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                      @click="removeStoreFromGroup"
                    >
                      <i :class="['fas mr-1', removingStore ? 'fa-sync fa-spin' : 'fa-check']"></i>
                      Yes
                    </button>
                    <button
                      type="button"
                      :disabled="removingStore"
                      class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 focus:outline-none dark:text-gray-300 dark:hover:bg-gray-700 ml-1 disabled:opacity-50"
                      @click="storeToRemove = null"
                    >
                      <i class="fas fa-times mr-1"></i>
                      No
                    </button>
                  </template>
                  <!-- Normal state: show Remove for non-OWNER stores -->
                  <button
                    v-else-if="store.role !== 'OWNER'"
                    type="button"
                    class="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 focus:outline-none dark:text-red-400 dark:hover:bg-red-900/20"
                    title="Remove store from group"
                    @click="confirmRemoveStore(store)"
                  >
                    <i class="fas fa-trash mr-1"></i>
                    Remove
                  </button>
                  <span v-else class="text-xs text-gray-400 dark:text-gray-500">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          v-if="storesPagination.total > storesPagination.perPage"
          class="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400"
        >
          <span>
            Showing {{ groupStores.length }} of {{ storesPagination.total }} stores
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              :disabled="!storesPagination.hasPrev"
              class="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
              @click="changeStoresPage(storesPagination.page - 1)"
            >
              Previous
            </button>
            <span>Page {{ storesPagination.page }}</span>
            <button
              type="button"
              :disabled="!storesPagination.hasNext"
              class="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-700"
              @click="changeStoresPage(storesPagination.page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </modal-view>

    <!-- Generate Join Code Modal -->
    <modal-view
      v-if="showJoinCodeModal && joinCodeTarget"
      title="Generate Join Code"
      :custom-max-width="'500px'"
      @close-modal="closeJoinCodeModal"
    >
      <div class="px-6 pb-8 space-y-5">
        <div v-if="!generatedCode" class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-300">
            Create a join code to invite stores to join
            <span class="font-semibold">"{{ joinCodeTarget.name }}"</span>.
          </p>
          <div>
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Code Expires In
            </label>
            <hs-select
              v-model="joinCodeExpiresInHours"
              :options="joinCodeExpiryOptions"
            />
          </div>
          <div
            v-if="joinCodeError"
            class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200"
          >
            {{ joinCodeError }}
          </div>
          <div class="flex items-center justify-end gap-3 pt-2">
            <hs-button
              variant="secondary"
              @click="closeJoinCodeModal"
            >
              Cancel
            </hs-button>
            <hs-button
              variant="primary"
              icon="key"
              :loading="joinCodeLoading"
              :disabled="joinCodeLoading"
              @click="handleGenerateJoinCode"
            >
              Generate Code
            </hs-button>
          </div>
        </div>

        <!-- Success: show generated code -->
        <div v-else class="space-y-4">
          <div
            class="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-900/20"
          >
            <p
              class="text-sm font-semibold text-green-900 dark:text-green-100 mb-1"
            >
              ✅ Join code generated!
            </p>
            <p class="text-sm text-green-800 dark:text-green-200">
              Share this code with stores you want to invite to the group.
            </p>
          </div>
          <div
            class="rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 dark:border-blue-800 dark:from-blue-900/30 dark:to-indigo-900/30"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-3"
            >
              🔑 Join Code
            </p>
            <div class="flex items-center gap-3 mb-3">
              <code
                class="flex-1 text-4xl font-mono font-bold text-blue-900 dark:text-blue-100 tracking-widest select-all"
              >
                {{ generatedCode.code }}
              </code>
              <button
                type="button"
                :title="codeCopied ? 'Copied!' : 'Copy to clipboard'"
                class="inline-flex items-center justify-center rounded-lg border-2 border-blue-300 bg-white px-4 py-3 text-lg font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all dark:border-blue-700 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-800"
                @click="copyJoinCode"
              >
                <i
                  :class="[
                    'fas transition-all',
                    codeCopied
                      ? 'fa-check text-green-600 dark:text-green-400'
                      : 'fa-copy',
                  ]"
                ></i>
                <span class="ml-2 text-sm">{{
                  codeCopied ? "Copied!" : "Copy"
                }}</span>
              </button>
            </div>
            <div class="flex items-center gap-2">
              <i
                class="fas fa-clock text-blue-600 dark:text-blue-400 text-xs"
              ></i>
              <p
                v-if="generatedCode.expires_at"
                class="text-xs font-medium text-blue-700 dark:text-blue-300"
              >
                Expires: {{ formatDate(generatedCode.expires_at) }}
              </p>
              <p
                v-else
                class="text-xs font-medium text-blue-700 dark:text-blue-300"
              >
                ⏳ Never expires
              </p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-3">
            <button
              type="button"
              class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="closeJoinCodeModal"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </modal-view>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import ModalView from "@/components/modals/ModalView.vue";
import { HsButton, HsInput, HsMetricCard, HsSelect } from "@/components/ui";

export default {
  name: "AdminGroupsView",
  components: {
    HsButton,
    HsInput,
    HsMetricCard,
    HsSelect,
    ModalView,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      searchTimeout: null,

      // Create modal
      showCreateModal: false,
      createLoading: false,
      createError: "",
      createForm: { name: "", description: "" },

      // Edit modal
      showEditModal: false,
      editLoading: false,
      editError: "",
      editTarget: null,
      editForm: { name: "", description: "" },

      // Delete modal
      showDeleteModal: false,
      deleteLoading: false,
      deleteError: "",
      deleteTarget: null,

      // Join code modal
      showJoinCodeModal: false,
      joinCodeLoading: false,
      joinCodeError: "",
      joinCodeTarget: null,
      joinCodeExpiresInHours: 168,
      joinCodeExpiryOptions: [
        { label: "24 hours", value: 24 },
        { label: "3 days", value: 72 },
        { label: "7 days", value: 168 },
        { label: "30 days", value: 720 },
        { label: "Never expires", value: null },
      ],
      generatedCode: null,
      codeCopied: false,

      // Stores modal
      showStoresModal: false,
      selectedGroupForStores: null,
      groupStores: [],
      storesLoading: false,
      storesError: "",
      storesSearch: "",
      storesSearchTimeout: null,
      storesPagination: {
        page: 1,
        perPage: 20,
        total: 0,
        pages: 0,
        hasNext: false,
        hasPrev: false,
      },
      storeToRemove: null,
      removingStore: false,
    };
  },
  computed: {
    groups() {
      return this.$store.getters["adminGroups/groups"];
    },
    loading() {
      return this.$store.getters["adminGroups/loading"];
    },
    total() {
      return this.$store.getters["adminGroups/total"];
    },
    currentPage() {
      return this.$store.getters["adminGroups/currentPage"];
    },
    perPage() {
      return this.$store.getters["adminGroups/perPage"];
    },
    filteredGroups() {
      if (!this.search.trim()) return this.groups;
      const q = this.search.toLowerCase();
      return this.groups.filter((g) =>
        g.name?.toLowerCase().includes(q) ||
        g.description?.toLowerCase().includes(q)
      );
    },
    summaryStats() {
      return [
        { label: "Total Groups", value: this.total || this.groups.length, icon: "users", tone: "primary" },
        {
          label: "Showing",
          value: this.filteredGroups.length,
          icon: "eye",
          tone: "success",
        },
        { label: "Page", value: this.currentPage, icon: "list", tone: "info" },
      ];
    },
  },
  mounted() {
    this.loadGroups();
  },
  methods: {
    async loadGroups(params = {}) {
      try {
        await this.$store.dispatch("adminGroups/fetchGroups", params);
      } catch (err) {
        toast.error(
          err?.response?.data?.error?.message || "Failed to load groups."
        );
      }
    },
    refreshData() {
      this.search = "";
      this.loadGroups();
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // Search is done client-side on the loaded page; for server-side, dispatch fetch with search param
        this.loadGroups({ search: this.search || undefined });
      }, 400);
    },
    changePage(page) {
      this.$store.commit("adminGroups/SET_PAGE", page);
      this.loadGroups();
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    // Create
    openCreateModal() {
      this.createForm = { name: "", description: "" };
      this.createError = "";
      this.showCreateModal = true;
    },
    async handleCreate() {
      const name = this.createForm.name.trim();
      if (!name) {
        this.createError = "Group name is required.";
        return;
      }
      this.createLoading = true;
      this.createError = "";
      try {
        await this.$store.dispatch("adminGroups/createGroup", {
          name,
          description: this.createForm.description.trim() || null,
        });
        toast.success(`Group "${name}" created successfully!`);
        this.showCreateModal = false;
        this.loadGroups();
      } catch (err) {
        this.createError =
          err?.response?.data?.error?.message ||
          "Unable to create group. Please try again.";
      } finally {
        this.createLoading = false;
      }
    },

    // Edit
    openEditModal(group) {
      this.editTarget = group;
      this.editForm = {
        name: group.name || "",
        description: group.description || "",
      };
      this.editError = "";
      this.showEditModal = true;
    },
    async handleUpdate() {
      const name = this.editForm.name.trim();
      if (!name) {
        this.editError = "Group name is required.";
        return;
      }
      this.editLoading = true;
      this.editError = "";
      try {
        await this.$store.dispatch("adminGroups/updateGroup", {
          groupId: this.editTarget.id,
          payload: {
            name,
            description: this.editForm.description.trim() || null,
          },
        });
        toast.success(`Group "${name}" updated successfully!`);
        this.showEditModal = false;
        this.loadGroups();
      } catch (err) {
        this.editError =
          err?.response?.data?.error?.message ||
          "Unable to update group. Please try again.";
      } finally {
        this.editLoading = false;
      }
    },

    // Delete
    openDeleteModal(group) {
      this.deleteTarget = group;
      this.deleteError = "";
      this.showDeleteModal = true;
    },
    async handleDelete() {
      this.deleteLoading = true;
      this.deleteError = "";
      try {
        await this.$store.dispatch(
          "adminGroups/deleteGroup",
          this.deleteTarget.id
        );
        toast.success(`Group "${this.deleteTarget.name}" deleted.`);
        this.showDeleteModal = false;
        this.loadGroups();
      } catch (err) {
        this.deleteError =
          err?.response?.data?.error?.message ||
          "Unable to delete group. Please try again.";
      } finally {
        this.deleteLoading = false;
      }
    },

    // Join Code
    openJoinCodeModal(group) {
      this.joinCodeTarget = group;
      this.joinCodeError = "";
      this.generatedCode = null;
      this.codeCopied = false;
      this.joinCodeExpiresInHours = 168;
      this.showJoinCodeModal = true;
    },
    closeJoinCodeModal() {
      this.showJoinCodeModal = false;
      this.generatedCode = null;
    },
    async handleGenerateJoinCode() {
      this.joinCodeLoading = true;
      this.joinCodeError = "";
      try {
        const payload = {};
        if (this.joinCodeExpiresInHours) {
          payload.expires_in_hours = this.joinCodeExpiresInHours;
        }
        const data = await this.$store.dispatch(
          "adminGroups/generateJoinCode",
          {
            groupId: this.joinCodeTarget.id,
            payload,
          }
        );
        this.generatedCode = data;
        toast.success("Join code generated successfully!");
      } catch (err) {
        this.joinCodeError =
          err?.response?.data?.error?.message ||
          "Unable to generate join code. Please try again.";
      } finally {
        this.joinCodeLoading = false;
      }
    },
    async copyJoinCode() {
      if (!this.generatedCode?.code) return;
      try {
        await navigator.clipboard.writeText(this.generatedCode.code);
        this.codeCopied = true;
        toast.success("Code copied to clipboard!");
        setTimeout(() => {
          this.codeCopied = false;
        }, 2000);
      } catch {
        toast.error("Failed to copy code.");
      }
    },

    // Stores
    openStoresModal(group) {
      this.selectedGroupForStores = group;
      this.groupStores = [];
      this.storesError = "";
      this.storesSearch = "";
      this.storeToRemove = null;
      this.removingStore = false;
      this.storesPagination = {
        page: 1,
        perPage: 20,
        total: 0,
        pages: 0,
        hasNext: false,
        hasPrev: false,
      };
      this.showStoresModal = true;
      this.loadGroupStores();
    },
    closeStoresModal() {
      this.showStoresModal = false;
      clearTimeout(this.storesSearchTimeout);
    },
    async loadGroupStores() {
      if (!this.selectedGroupForStores || !this.showStoresModal) return;
      this.storesLoading = true;
      this.storesError = "";
      try {
        const params = {
          page: this.storesPagination.page,
          per_page: this.storesPagination.perPage,
        };
        if (this.storesSearch.trim()) {
          params.search_query = this.storesSearch.trim();
          params.search_by = "name";
        }
        const response = await http.get(
          `/v2/groups/${this.selectedGroupForStores.id}/stores`,
          { params }
        );
        const data = response?.data || {};
        this.groupStores = data.items || [];
        this.storesPagination = {
          page: data.page || 1,
          perPage: data.per_page || 20,
          total: data.total || 0,
          pages: data.pages || 0,
          hasNext: data.has_next || false,
          hasPrev: data.has_prev || false,
        };
      } catch (err) {
        this.storesError =
          err?.response?.data?.error?.message || "Unable to load stores.";
      } finally {
        this.storesLoading = false;
      }
    },
    onStoresSearchInput() {
      clearTimeout(this.storesSearchTimeout);
      this.storesSearchTimeout = setTimeout(() => {
        this.storesPagination.page = 1;
        this.loadGroupStores();
      }, 400);
    },
    changeStoresPage(page) {
      this.storesPagination.page = page;
      this.loadGroupStores();
    },
    getStoreRoleBadgeClass(role) {
      const classes = {
        OWNER: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
        ADMIN: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
        MEMBER: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      };
      return classes[role] || classes.MEMBER;
    },
    confirmRemoveStore(store) {
      this.storeToRemove = store;
    },
    async removeStoreFromGroup() {
      if (!this.storeToRemove || !this.selectedGroupForStores) return;
      this.removingStore = true;
      try {
        await http.delete(
          `/v2/groups/${this.selectedGroupForStores.id}/stores/${this.storeToRemove.store_id}`
        );
        toast.success(`"${this.storeToRemove.name}" removed from group.`);
        this.storeToRemove = null;
        await this.loadGroupStores();
      } catch (err) {
        toast.error(
          err?.response?.data?.error?.message ||
            "Unable to remove store from group."
        );
      } finally {
        this.removingStore = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-groups__stats {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.admin-groups__panel {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

.admin-groups__panel :deep(table) {
  border-collapse: collapse;
}

.admin-groups__panel :deep(th) {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@media (max-width: 760px) {
  .admin-groups__stats {
    grid-template-columns: 1fr;
  }
}
</style>
